import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { UserPlus } from 'lucide-react';
import WelcomeEmailStatus from './WelcomeEmailStatus';

const MissingLoggedIn = () => {
    // Existing state for missing contributions
    const [data, setData] = useState({ years: [], companies: [] });
    const [selectedYear, setSelectedYear] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reminderStatus, setReminderStatus] = useState({});
    const [sendingBulkReminders, setSendingBulkReminders] = useState(false);
    
    // New state for never logged in users
    const [neverLoggedInUsers, setNeverLoggedInUsers] = useState([]);
    const [loadingNeverLoggedIn, setLoadingNeverLoggedIn] = useState(true);
    const [welcomeEmailStatus, setWelcomeEmailStatus] = useState({});
    const [showWelcomeEmailSection, setShowWelcomeEmailSection] = useState(false);

    // Fetch users who have never logged in
    useEffect(() => {
        fetch(`${boardMemberData.restURL}board-member/v1/never-logged-in-users`, {
            headers: {
                'X-WP-Nonce': boardMemberData.nonce,
            },
        })
            .then(response => {
                if (!response.ok) {
                    if (response.status === 404) {
                        return []; // Handle case where endpoint might not exist yet
                    }
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                if (Array.isArray(data) && data.length > 0) {
                    setNeverLoggedInUsers(data);
                    setShowWelcomeEmailSection(true);
                }
                setLoadingNeverLoggedIn(false);
            })
            .catch(error => {
                console.error('Failed to load never logged in users:', error);
                setLoadingNeverLoggedIn(false);
            });
    }, []);



    // New functions for welcome emails
    const sendWelcomeEmail = (userId) => {
        setWelcomeEmailStatus(prevStatus => ({
            ...prevStatus,
            [userId]: {
                status: 'sending',
                message: __('Sending...')
            }
        }));

        fetch(`${boardMemberData.restURL}board-member/v1/send-welcome-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': boardMemberData.nonce,
            },
            body: JSON.stringify({ user_id: userId }),
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setWelcomeEmailStatus(prevStatus => ({
                        ...prevStatus,
                        [userId]: {
                            status: 'success',
                            message: __('Welcome email sent!')
                        }
                    }));

                    // Remove user from the list after successful email
                    setNeverLoggedInUsers(prevUsers => 
                        prevUsers.filter(user => user.ID !== userId)
                    );
                } else {
                    setWelcomeEmailStatus(prevStatus => ({
                        ...prevStatus,
                        [userId]: {
                            status: 'error',
                            message: __('Failed to send welcome email')
                        }
                    }));
                }
            })
            .catch(() => {
                setWelcomeEmailStatus(prevStatus => ({
                    ...prevStatus,
                    [userId]: {
                        status: 'error',
                        message: __('Failed to send welcome email')
                    }
                }));
            });
    };

    const sendAllWelcomeEmails = () => {
        if (window.confirm(__('Are you sure you want to send welcome emails to all users who have never logged in?'))) {
            fetch(`${boardMemberData.restURL}board-member/v1/send-all-welcome-emails`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
            })
                .then(response => response.json())
                .then(data => {
                    alert(data.message);
                })
                .catch(() => {
                    alert(__('Failed to send welcome emails'));
                });
        }
    };

    if (loadingNeverLoggedIn) {
        return <p>{__('Loading...')}</p>;
    }

    if (error) {
        return <p>{error}</p>;
    }



    return (
        <div>
            {/* Never Logged In Users Section */}
            {showWelcomeEmailSection && !loadingNeverLoggedIn && neverLoggedInUsers.length > 0 && (
                <div className="mt-10">
                    <div className="flex justify-between items-center mb-4">
                        <h2>{__('Users Who Have Never Logged In')}</h2>
                        <button
                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
                            onClick={sendAllWelcomeEmails}
                        >
                            <UserPlus className="mr-2 h-5 w-5" />
                            {__('Send Welcome Emails to All')}
                        </button>
                    </div>
                    
                    <WelcomeEmailStatus />
                    
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-yellow-700 mb-0">
                                    {__('These users have never logged into the system. Send them a welcome email with login instructions.')}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {neverLoggedInUsers.reduce((companies, user) => {
                            // Group users by company
                            const companyId = user.company_id;
                            if (!companies[companyId]) {
                                companies[companyId] = {
                                    companyId,
                                    companyName: user.company,
                                    users: []
                                };
                            }
                            companies[companyId].users.push(user);
                            console.log('Companies:', companies);
                            return companies;
                        }, []).map(company => (
                            <div key={company.companyId} className="bg-white shadow rounded-lg p-6 border-l-4 border-red-500">
                                <div className="flex justify-between items-center mb-4">
                                    <div>
                                        <h3 dangerouslySetInnerHTML={{ __html: company.companyName }} className="text-lg font-semibold" />
                                        
                                    </div>
                                    <div>
                                        {/* Send welcome email for whole company */}
                                        <button
                                            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
                                            onClick={() => {
                                                company.users.forEach(user => 
                                                    sendWelcomeEmail(user.ID)
                                                );
                                            }} 
                                        >
                                            <UserPlus className="mr-2 h-5 w-5" />
                                            {__('Send Company Welcome Emails')}
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full table-auto">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="px-6 py-3 text-left">{__('Username')}</th>
                                                <th className="px-6 py-3 text-left">{__('Email')}</th>
                                                <th className="px-6 py-3 text-left">{__('Full Name')}</th>
                                                <th className="px-6 py-3 text-left">{__('Action')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {company.users.map(user => {
                                                const welcomeResult = welcomeEmailStatus[user.ID];
                                                
                                                return (
                                                    <tr key={user.ID} className="border-t bg-red-50">
                                                        <td className="px-6 py-4">{user.username}</td>
                                                        <td className="px-6 py-4">{user.email}</td>
                                                        <td className="px-6 py-4">{user.name}</td>
                                                        <td className="px-6 py-4">
                                                            <button
                                                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
                                                                onClick={() => sendWelcomeEmail(user.ID)}
                                                                disabled={welcomeResult?.status === 'success'}
                                                            >
                                                                <UserPlus className="mr-2 h-5 w-5" />
                                                                {__('Send Welcome Email')}
                                                            </button>
                                                            {welcomeResult && (
                                                                <span className={`ml-2 text-sm ${
                                                                    welcomeResult.status === 'success' ? 'text-green-600' : 
                                                                    welcomeResult.status === 'sending' ? 'text-blue-600' : 'text-red-600'
                                                                }`}>
                                                                    {welcomeResult.message}
                                                                </span>
                                                            )} 
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MissingLoggedIn;